import React from 'react';
import type { AgentPersona } from '../constants';
import type { BrandTeamAgentRun, BrandTeamAgentArtifact } from '../../../../services/brandTeamService';

type AgentState = 'queued' | 'active' | 'done' | 'failed';

interface AgentCardProps {
  persona: AgentPersona;
  run?: BrandTeamAgentRun;
  artifact?: BrandTeamAgentArtifact;
}

function deriveState(run?: BrandTeamAgentRun): AgentState {
  if (!run) return 'queued';
  if (run.status === 'running') return 'active';
  if (run.status === 'completed') return 'done';
  if (run.status === 'failed') return 'failed';
  return 'queued';
}

function deriveStatusText(state: AgentState, persona: AgentPersona, run?: BrandTeamAgentRun, artifact?: BrandTeamAgentArtifact): string {
  if (state === 'queued') return 'Waiting...';
  if (state === 'failed') return run?.errorMessage || 'An error occurred';
  if (state === 'active') return `"Working on your ${persona.role.toLowerCase()} analysis..."`;
  const summary = (artifact?.payload?.summary || artifact?.payload?.managerSummary) as string | undefined;
  if (summary && typeof summary === 'string') return summary.length > 80 ? summary.slice(0, 77) + '...' : summary;
  return 'Complete';
}

const stateStyles: Record<AgentState, { card: string; statusColor: string }> = {
  queued: { card: 'bg-gray-50 border-transparent', statusColor: 'text-gray-400' },
  active: { card: 'bg-primary-50 border-primary-200', statusColor: 'text-brand-purple italic' },
  done: { card: 'bg-green-50 border-green-200', statusColor: 'text-green-600' },
  failed: { card: 'bg-red-50 border-red-200', statusColor: 'text-red-600' },
};

const AgentCard: React.FC<AgentCardProps> = ({ persona, run, artifact }) => {
  const state = deriveState(run);
  const styles = stateStyles[state];
  const statusText = deriveStatusText(state, persona, run, artifact);
  const Icon = persona.icon;

  return (
    <div className={`flex items-center gap-3 rounded-xl border-[1.5px] p-3 transition-all duration-200 ${styles.card} ${state === 'queued' ? 'opacity-60' : ''}`}>
      <div className={`relative flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${
        state === 'active'
          ? `bg-gradient-to-br ${persona.gradientFrom} ${persona.gradientTo}`
          : state === 'done'
          ? 'bg-green-500'
          : state === 'failed'
          ? 'bg-red-500'
          : 'bg-gray-300'
      }`}>
        <Icon className="h-4 w-4 text-white" />
        {state === 'active' && (
          <span className="absolute inset-0 rounded-full border-2 border-brand-purple animate-ping opacity-30" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-bold text-text-dark">{persona.name}</span>
          <span className="text-[10px] text-body-grey">{persona.role}</span>
        </div>
        <div className={`mt-0.5 text-[11px] leading-snug truncate ${styles.statusColor}`}>
          {statusText}
        </div>
      </div>
    </div>
  );
};

export default AgentCard;
